<script setup lang="ts">
import { computed } from 'vue'

type RadioSize = 'sm' | 'md' | 'lg'

const props = withDefaults(defineProps<{
  modelValue?: string | number | null
  value: string | number
  label?: string
  disabled?: boolean
  size?: RadioSize
  /** 同一组单选使用相同 name，支持浏览器原生方向键切换 */
  name?: string
}>(), {
  modelValue: null,
  label: '',
  disabled: false,
  size: 'md',
  name: undefined
})

const emit = defineEmits<{
  'update:modelValue': [value: string | number]
  'change': [value: string | number]
}>()

const isChecked = computed(() => props.modelValue === props.value)

const wrapperClass = computed(() => [
  'base-radio',
  `base-radio--${props.size}`,
  {
    'base-radio--disabled': props.disabled,
    'base-radio--checked': isChecked.value
  }
])

function handleChange(): void {
  if (props.disabled) return
  emit('update:modelValue', props.value)
  emit('change', props.value)
}
</script>

<template>
  <label :class="wrapperClass">
    <!-- 透明单选铺满整个 label：点文字、点圆圈都能选中，不再只认 16px 小圆点 -->
    <input
      type="radio"
      class="base-radio__input"
      :name="name"
      :checked="isChecked"
      :disabled="disabled"
      @change="handleChange"
    />
    <span class="base-radio__input-wrapper">
      <span class="base-radio__circle">
        <span class="base-radio__dot" />
      </span>
    </span>
    <span v-if="label || $slots.default" class="base-radio__label">
      <slot>{{ label }}</slot>
    </span>
  </label>
</template>

<style lang="scss" scoped>
.base-radio {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-3);
  cursor: pointer;
  font-size: var(--text-sm);
  color: var(--text-secondary);

  &--disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &__input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__input {
    position: absolute;
    inset: 0;
    z-index: 1;
    width: 100%;
    height: 100%;
    margin: 0;
    opacity: 0;
    cursor: pointer;

    &:disabled {
      cursor: not-allowed;
    }
  }

  &__circle {
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--bg-tertiary);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-full);
    transition: all var(--transition-fast);

    .base-radio--sm & {
      width: 16px;
      height: 16px;
    }

    .base-radio--md & {
      width: 18px;
      height: 18px;
    }

    .base-radio--lg & {
      width: 20px;
      height: 20px;
    }
  }

  &__dot {
    border-radius: var(--radius-full);
    background: var(--white);
    transition: all var(--transition-fast);

    .base-radio--sm & {
      width: 6px;
      height: 6px;
    }

    .base-radio--md & {
      width: 8px;
      height: 8px;
    }

    .base-radio--lg & {
      width: 10px;
      height: 10px;
    }

    .base-radio:not(.base-radio--checked) & {
      opacity: 0;
      transform: scale(0);
    }

    .base-radio--checked & {
      opacity: 1;
      transform: scale(1);
    }
  }

  &:hover:not(.base-radio--disabled) &__circle {
    border-color: var(--primary);
  }

  &--checked &__circle {
    background: var(--primary);
    border-color: var(--primary);
  }

  &__label {
    user-select: none;
  }
}
</style>
